import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

export type EntityType = 'sales_opportunity' | 'customer_visit' | 'brand_work' | 'task' | 'customer' | 'quote';

export interface Reminder {
  id: number;
  entity_type: EntityType;
  entity_id: number;
  reminder_date: string;
  reminder_time?: string;
  title: string;
  description?: string;
  is_completed: boolean;
  completed_at?: string;
  completed_by?: number;
  notify_email: boolean;
  notify_crm: boolean;
  assigned_to?: number;
  assigned_to_name?: string;
  assigned_to_surname?: string;
  created_by: number;
  created_by_name?: string;
  created_by_surname?: string;
  created_at: string;
  updated_at: string;
  company_id: number;
}

export interface CreateReminderParams {
  entity_type: EntityType;
  entity_id: number;
  reminder_date: string;
  reminder_time?: string;
  title: string;
  description?: string;
  notify_email?: boolean;
  notify_crm?: boolean;
  assigned_to?: number;
}

export interface UpdateReminderParams {
  reminder_date?: string;
  reminder_time?: string;
  title?: string;
  description?: string;
  notify_email?: boolean;
  notify_crm?: boolean;
  assigned_to?: number;
  is_completed?: boolean;
}

// Hatırlatıcı oluştur
export const useCreateReminder = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/reminders/create';
  return useMutation(
    (payload: CreateReminderParams) => {
      return axios.post(`${requestUrl}`, payload);
    },
    { onError, onSuccess },
  );
};

// Hatırlatıcı güncelle
export const useUpdateReminder = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/reminders';
  return useMutation(
    (payload: { id: number } & UpdateReminderParams) => {
      const { id, ...data } = payload;
      return axios.put(`${requestUrl}/${id}`, data);
    },
    { onError, onSuccess },
  );
};

// Hatırlatıcı sil
export const useDeleteReminder = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/reminders';
  return useMutation(
    (id: number) => {
      return axios.delete(`${requestUrl}/${id}`);
    },
    { onError, onSuccess },
  );
};

// Hatırlatıcı tamamla
export const useCompleteReminder = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/reminders';
  return useMutation(
    (id: number) => {
      return axios.post(`${requestUrl}/${id}/complete`);
    },
    { onError, onSuccess },
  );
};

// Entity'ye ait hatırlatıcıları getir
export const useRemindersByEntity = (entity_type: EntityType, entity_id: number, enabled: boolean = true, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/reminders/by-entity';
  const fetchData = async () => {
    const res = await axios.get<{ success: boolean; results: Reminder[] }>(`${requestUrl}`, {
      params: { entity_type, entity_id },
    });
    return res?.data?.results || [];
  };
  return useQuery(['reminders-by-entity', entity_type, entity_id], fetchData, {
    refetchOnWindowFocus: false,
    enabled: enabled && !!entity_id,
    onError,
    staleTime: 30000,
    retry: false,
  });
};

// Kullanıcının hatırlatıcılarını getir
export const useMyReminders = (
  filters: {
    is_completed?: boolean;
    start_date?: string;
    end_date?: string;
    entity_type?: EntityType;
  } = {},
  onError?: any,
) => {
  const requestUrl = REACT_APP_API_URL + '/reminders/my-reminders';
  const fetchData = async () => {
    const params: any = {};
    if (filters.is_completed !== undefined) params.is_completed = String(filters.is_completed);
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.entity_type) params.entity_type = filters.entity_type;

    const res = await axios.get<{ success: boolean; results: Reminder[] }>(`${requestUrl}`, { params });
    return res?.data?.results || [];
  };
  return useQuery(['my-reminders', filters], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 30000,
    retry: false,
  });
};

// Entity type label mapping
export const entityTypeLabels: Record<EntityType, string> = {
  sales_opportunity: 'Satış Fırsatı',
  customer_visit: 'Müşteri Ziyareti',
  brand_work: 'Marka Çalışması',
  task: 'Görev',
  customer: 'Müşteri',
  quote: 'Teklif',
};
